<template>
    <nav
        class="layout-breadcrumb tw:flex tw:justify-between"
        :class="[
            { 'layout-breadcrumb--corporate': isCorporate },
            isSingleLineTrail ? SINGLE_LINE_NAV_CLASS : null,
        ]"
    >
        <ol :class="isSingleLineTrail ? SINGLE_LINE_TRAIL_OL_CLASS : 'tw:w-4/5'">
            <li v-if="!isHome">
                <router-link :to="home.to" style="color: inherit">
                    <i :class="home.icon" />
                </router-link>
            </li>
            <li v-if="!isHome && breadcrumbItems.length > 0" class="layout-breadcrumb-chevron">/</li>
            <template v-if="!isHome">
                <template v-for="(crumb, i) in breadcrumbItems" :key="i">
                    <li
                        class="layout-breadcrumb-crumb tw:normal-case tw:min-w-0 tw:flex tw:items-center"
                        :class="singleLineCrumbClass(i, breadcrumbItems.length)"
                    >
                        <router-link
                            v-if="crumb.to && i !== breadcrumbItems.length - 1"
                            :to="crumb.to"
                            class="tw:block tw:min-w-0 tw:max-w-[10rem] tw:md:max-w-[24rem] tw:truncate"
                            :title="isSingleLineTrail ? crumb.text : undefined"
                        >
                            {{ crumb.text }}
                        </router-link>
                        <span
                            v-else
                            class="tw:block tw:min-w-0 tw:max-w-[10rem] tw:md:max-w-[24rem] tw:truncate"
                            :title="isSingleLineTrail ? crumb.text : undefined"
                        >{{ crumb.text }}</span>
                    </li>
                    <li v-if="i !== breadcrumbItems.length - 1" class="layout-breadcrumb-chevron">/</li>
                </template>
            </template>
        </ol>
        <div
            class="layout-breadcrumb-actions tw:flex tw:min-w-0 tw:items-center tw:justify-end tw:gap-2"
            :class="isSingleLineTrail ? SINGLE_LINE_ACTIONS_CLASS : 'tw:flex-1'"
        >
            <portal-target name="breadcrumb-activity-nav" />
            <portal-target name="breadcrumb-actions" />
        </div>
    </nav>
    <Divider />
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useEdverumClient } from '@/composables/useEdverumClient.js';

/**
 * Opt-in single-line trail (route meta `singleLineBreadcrumb`, used by the course Enrollments roster).
 * Applies from 768px (`md`, the iPad-portrait width where `$isMobile` is still true) up to 1199px
 * (`max-xl`, the `$isTablet` upper bound). In that range every crumb stays on one row: the first
 * crumb (section) and the last crumb (current page) keep their width, and the crumbs between them
 * (the course name) absorb the squeeze with an ellipsis; `title` exposes the full text. Phones
 * (< 768px) and desktop (>= 1200px) keep the default wrapping trail. The `!` overrides are needed
 * because the unlayered `_breadcrumb.scss` rules (flex-wrap, mobile align-items) beat layered utilities.
 */
const SINGLE_LINE_NAV_CLASS =
    'layout-breadcrumb--single-line-trail tw:w-full tw:min-w-0 tw:md:max-xl:flex-nowrap tw:md:max-xl:items-center!';

const SINGLE_LINE_TRAIL_OL_CLASS = [
    'tw:w-4/5',
    'tw:md:max-xl:w-auto tw:md:max-xl:min-w-0 tw:md:max-xl:max-w-[calc(100%-5.5rem)] tw:md:max-xl:shrink',
    'tw:md:max-xl:flex-nowrap! tw:md:max-xl:overflow-hidden',
].join(' ');

const SINGLE_LINE_ACTIONS_CLASS = 'tw:flex-1 tw:md:max-xl:flex-none tw:md:max-xl:shrink-0 tw:md:max-xl:pl-3';

const SINGLE_LINE_FIXED_CRUMB_CLASS = 'tw:md:max-xl:shrink-0';

const route = useRoute();
const store = useStore();
const { isCorporate } = useEdverumClient();
const home = { icon: 'pi pi-home', to: '/' };
const fallbackBreadcrumbs = ref([]);

const isHome = computed(() => route.path === '/');

const isSingleLineTrail = computed(() => Boolean(route.meta?.singleLineBreadcrumb));

/**
 * In the single-line trail, keep the first and last crumbs at full width so only the middle
 * (context) crumbs truncate.
 */
function singleLineCrumbClass(index, total) {
    if (!isSingleLineTrail.value) {
        return null;
    }

    return index === 0 || index === total - 1 ? SINGLE_LINE_FIXED_CRUMB_CLASS : null;
}

const storeBreadcrumbs = computed(() => store.state.Breadcrumbs.breadcrumbs);

const breadcrumbItems = computed(() => {
    if (storeBreadcrumbs.value && storeBreadcrumbs.value.length > 0) {
        return storeBreadcrumbs.value;
    }
    return fallbackBreadcrumbs.value;
});

function setFallbackBreadcrumbs() {
    if (route.meta.breadcrumb) {
        fallbackBreadcrumbs.value = route.meta.breadcrumb.map(text => ({ text }));
        return;
    }

    fallbackBreadcrumbs.value = route.fullPath
        .split('?')[0]
        .split('/')
        .filter(item => item !== '')
        .filter(item => isNaN(Number(item)))
        .map(item => {
            const withSpaces = item.replace(/-/g, ' ');
            return { text: withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1) };
        });
}

watch(
    route,
    () => {
        setFallbackBreadcrumbs();
    },
    { immediate: true }
);
</script>
